
import { useTranslation } from "react-i18next";
import { useUiStore } from "@/store/ui";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LanguageSwitcher } from "@/components/i18n/language-switcher";
import { useTheme } from "next-themes";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Package,
  Store,
  Camera,
  MapPin,
  ShieldCheck,
  Rocket,
  PackageCheck,
  BarChart3,
  ArrowRight,
  Sparkles,
  Droplet,
  Recycle,
  Moon,
  Sun,
  Monitor,
} from "lucide-react";
import { Logo } from "@/components/brand/logo";
import { BrandWordmark } from "@/components/brand/wordmark";

/**
 * Landing view — marketing surface that converts visitors into either
 * Customers (pickup/delivery) or Providers (merchants selling on the
 * marketplace). Copy is the v2 marketing brief.
 */
export function LandingView() {
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  const customerFeatures = [
    {
      icon: Camera,
      title: t("landing.customerFeature1Title"),
      desc: t("landing.customerFeature1Desc"),
    },
    {
      icon: MapPin,
      title: t("landing.customerFeature2Title"),
      desc: t("landing.customerFeature2Desc"),
    },
    {
      icon: ShieldCheck,
      title: t("landing.customerFeature3Title"),
      desc: t("landing.customerFeature3Desc"),
    },
  ];

  const merchantFeatures = [
    {
      icon: Rocket,
      title: t("landing.merchantFeature1Title"),
      desc: t("landing.merchantFeature1Desc"),
    },
    {
      icon: PackageCheck,
      title: t("landing.merchantFeature2Title"),
      desc: t("landing.merchantFeature2Desc"),
    },
    {
      icon: BarChart3,
      title: t("landing.merchantFeature3Title"),
      desc: t("landing.merchantFeature3Desc"),
    },
  ];

  return (
    <div className="flex min-h-screen flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/80 backdrop-blur-lg">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <button
            onClick={() => navigate("")}
            className="flex items-center gap-2"
            aria-label={t("landing.homeAria")}
          >
            <Logo className="h-8 w-8" />
            <BrandWordmark className="text-lg" />
          </button>
          <div className="flex items-center gap-1.5">
            <LanguageSwitcher compact />
            {/* Theme toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9" aria-label={t("theme.label")}>
                  {theme === "dark" ? <Moon className="h-4 w-4" /> : theme === "system" ? <Monitor className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuLabel className="text-xs text-muted-foreground">{t("theme.label")}</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setTheme("light")}><Sun className="mr-2 h-4 w-4" /> {t("theme.light")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}><Moon className="mr-2 h-4 w-4" /> {t("theme.dark")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}><Monitor className="mr-2 h-4 w-4" /> {t("theme.system")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" onClick={() => navigate("login")}>
              {t("landing.signIn")}
            </Button>
            <Button size="sm" onClick={() => navigate("register")}>
              {t("landing.getStarted")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="bg-dot-pattern absolute inset-0 opacity-60" />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <Logo className="mx-auto h-20 w-20 sm:h-24 sm:w-24" />
            <span className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <Sparkles className="h-3.5 w-3.5" /> {t("landing.heroBadge")}
            </span>
            <h1 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-5xl">
              <span className="text-foreground">{t("landing.heroHeadline1")}</span>
              <br />
              <span className="text-gold">{t("landing.heroHeadline2")}</span>
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base text-muted-foreground sm:text-lg">
              {t("landing.heroLead")}
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button size="lg" className="w-full sm:w-auto" onClick={() => navigate("register")}>
                <Package className="mr-2 h-4 w-4" /> {t("landing.customerCta")}
                <ArrowRight className="ml-2 h-4 w-4 rtl-flip" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => navigate("register")}
              >
                <Store className="mr-2 h-4 w-4" /> {t("landing.merchantCta")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1 — For Customers */}
      <section className="mx-auto w-full max-w-5xl px-4 py-14 sm:py-16">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <Package className="h-3.5 w-3.5" /> {t("landing.forCustomers")}
          </span>
          <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
            <span className="text-foreground">{t("landing.customerSectionTitle1")}</span>{" "}
            <span className="text-gold">{t("landing.customerSectionTitle2")}</span>
          </h2>
          <p className="mt-1 text-lg font-medium text-foreground">
            {t("landing.customerSectionLead")}
          </p>
          <p className="mt-3 text-sm text-muted-foreground sm:text-base">
            {t("landing.customerSectionBody")}
          </p>
        </div>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {customerFeatures.map((f, i) => (
            <Card key={i} className="p-5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="mt-3 font-semibold">{f.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Section 2 — For Merchants */}
      <section className="border-y border-border/60 bg-muted/30">
        <div className="mx-auto w-full max-w-5xl px-4 py-14 sm:py-16">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/15 px-3 py-1 text-xs font-semibold text-gold">
              <Store className="h-3.5 w-3.5" /> {t("landing.forMerchants")}
            </span>
            <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">
              <span className="text-foreground">{t("landing.merchantSectionTitle1")}</span>{" "}
              <span className="text-gold">{t("landing.merchantSectionTitle2")}</span>
            </h2>
            <p className="mt-1 text-lg font-medium text-foreground">
              {t("landing.merchantSectionLead")}
            </p>
            <p className="mt-3 text-sm text-muted-foreground sm:text-base">
              {t("landing.merchantSectionBody")}
            </p>
          </div>

          {/* ========================================================= */}
          {/* Exclusive Launch — dual water-brand announcement banner  */}
          {/* ========================================================= */}
          <div className="mt-8">
            {/* Section label */}
            <div className="mb-3 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-gold/20 via-accent/15 to-primary/20 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-foreground ring-1 ring-border/60">
                <Sparkles className="h-3.5 w-3.5 text-gold" />
                {t("landing.launchPartners")}
              </span>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {/* ---- AHLA — Bottled Water ---- */}
              <div
                className="launch-card group relative overflow-hidden rounded-2xl border border-gold/40 bg-gradient-to-br from-gold/10 via-amber-50/50 to-transparent p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-gold/60 hover:shadow-lg hover:shadow-gold/10 dark:from-gold/15 dark:via-amber-950/20 dark:to-transparent"
              >
                {/* Decorative corner glow */}
                <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gold/20 blur-3xl transition-all duration-500 group-hover:scale-110 group-hover:bg-gold/30" />
                {/* Faint water-drop watermark */}
                <Droplet className="pointer-events-none absolute -bottom-3 -left-2 h-20 w-20 text-gold/[0.06]" />

                <div className="relative flex items-center gap-4">
                  {/* Logo tile with pulsing glow + float */}
                  <div className="relative shrink-0">
                    <span className="launch-glow" data-accent="gold" />
                    <div className="launch-logo-float flex h-20 w-20 items-center justify-center rounded-2xl bg-white p-2.5 shadow-md ring-1 ring-gold/20 dark:bg-white/10 dark:ring-gold/30">
                      <img
                        src="/water/ahla-logo.png"
                        alt="AHLA premium spring drinking water"
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-gold/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gold ring-1 ring-gold/30">
                        <Droplet className="h-3 w-3" />
                        {t("landing.ahlaLaunchBadge")}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <Sparkles className="h-3 w-3 text-gold" />
                        {t("landing.exclusiveLaunch")}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-semibold leading-snug text-foreground">
                      <span className="text-gold-gradient font-extrabold">AHLA</span>{" "}
                      {t("landing.ahlaLaunchText")}
                    </p>
                  </div>
                </div>
              </div>

              {/* ---- ZAMZAM — Refilled Water ---- */}
              <div
                className="launch-card group relative overflow-hidden rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 via-teal-50/50 to-transparent p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-lg hover:shadow-primary/10 dark:from-primary/15 dark:via-teal-950/20 dark:to-transparent"
              >
                <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/20 blur-3xl transition-all duration-500 group-hover:scale-110 group-hover:bg-primary/30" />
                <Droplet className="pointer-events-none absolute -bottom-3 -left-2 h-20 w-20 text-primary/[0.06]" />

                <div className="relative flex items-center gap-4">
                  <div className="relative shrink-0">
                    <span className="launch-glow" data-accent="primary" />
                    <div className="launch-logo-float flex h-20 w-20 items-center justify-center rounded-2xl bg-white p-2.5 shadow-md ring-1 ring-primary/20 dark:bg-white/10 dark:ring-primary/30">
                      <img
                        src="/water/zamzam-logo.png"
                        alt="ZAMZAM purified mineral drinking water"
                        className="h-full w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary ring-1 ring-primary/30">
                        <Recycle className="h-3 w-3" />
                        {t("landing.zamzamLaunchBadge")}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-foreground/[0.06] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <Sparkles className="h-3 w-3 text-primary" />
                        {t("landing.exclusiveLaunch")}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-semibold leading-snug text-foreground">
                      <span className="font-extrabold text-primary">ZAMZAM</span>{" "}
                      {t("landing.zamzamLaunchText")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {merchantFeatures.map((f, i) => (
              <Card key={i} className="p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-3 font-semibold">{f.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA — Split Action */}
      <section className="mx-auto w-full max-w-5xl px-4 py-14 sm:py-16">
        <h2 className="text-center text-2xl font-bold tracking-tight sm:text-3xl">
          <span className="text-foreground">{t("landing.finalCtaTitle1")}</span>{" "}
          <span className="text-gold">{t("landing.finalCtaTitle2")}</span>
        </h2>
        <p className="mx-auto mt-2 max-w-xl text-center text-sm text-muted-foreground sm:text-base">
          {t("landing.finalCtaSubtitle")}
        </p>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {/* Customers */}
          <Card className="flex flex-col p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Package className="h-5 w-5" />
            </div>
            <h3 className="mt-3 text-lg font-semibold">{t("landing.forCustomers")}</h3>
            <p className="mt-1 flex-1 text-sm text-muted-foreground">
              {t("landing.customerFinalDesc")}
            </p>
            <Button className="mt-4 w-full" onClick={() => navigate("register")}>
              {t("landing.signUp")} <ArrowRight className="ml-2 h-4 w-4 rtl-flip" />
            </Button>
          </Card>
          {/* Providers */}
          <Card className="flex flex-col border-gold/30 p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold">
              <Store className="h-5 w-5" />
            </div>
            <h3 className="mt-3 text-lg font-semibold">{t("landing.forProviders")}</h3>
            <p className="mt-1 flex-1 text-sm text-muted-foreground">
              {t("landing.providerFinalDesc")}
            </p>
            <Button className="mt-4 w-full" onClick={() => navigate("register")}>
              {t("landing.applyNow")} <ArrowRight className="ml-2 h-4 w-4 rtl-flip" />
            </Button>
          </Card>
        </div>
      </section>

      {/* Footer (sticky to bottom) */}
      <footer className="mt-auto border-t border-border/60 bg-muted/30">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 px-4 py-6 text-sm text-muted-foreground sm:flex-row">
          <div className="flex items-center gap-2">
            <Logo className="h-6 w-6" />
            <span className="font-semibold text-foreground">{t("landing.footerLegal.copyright")}</span>
          </div>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <button onClick={() => navigate("legal/privacy")} className="hover:text-foreground hover:underline">
              {t("landing.footerLegal.privacy")}
            </button>
            <button onClick={() => navigate("legal/terms")} className="hover:text-foreground hover:underline">
              {t("landing.footerLegal.terms")}
            </button>
            <button onClick={() => navigate("legal/data-protection")} className="hover:text-foreground hover:underline">
              {t("landing.footerLegal.dataProtection")}
            </button>
            <button onClick={() => navigate("legal/support")} className="hover:text-foreground hover:underline">
              {t("landing.footerLegal.helpSupport")}
            </button>
          </nav>
        </div>
      </footer>
    </div>
  );
}
